/* eslint-disable no-unused-vars */
/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { IPage } from 'src/interfaces';
import { CommonDropdown, RegularButton, CommonInput } from 'pg-ui-library';

import {
  MainContainer,
  TopBar,
  SearchLink,
  ViewHistory,
  InvoiceTitle,
  SubText,
  InfoGrid,
  InfoRow,
  Label,
  Value,
  Status,
  StatusDot,
  Divider,
  AmountGrid,
  NotesBox,
  Attachment,
  AttachmentIcon,
} from './viewstyles';
import { InvoiceSubmmisionModal } from './modals/approveReject';
import { InvoiceRejectionModal } from './modals/rejectModal';
import { InvoiceDeleteModal } from './modals/deleteInvoice';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import InvoiceService from 'src/services/invoice.service';
import { Link, useNavigate, useParams } from 'react-router-dom';
import attachmentBlue from 'src/assets/attachment-blue.svg';
import { da } from 'date-fns/locale';
import { useLocalStorage } from 'src/hooks/useLocalStorage';

export const formatPeriodDate = (dateStr: string): string => {
  const date = new Date(dateStr);

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

const ViewInvoice: React.FC<IPage> = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenReject, setIsOpenReject] = useState<boolean>(false);
  const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);

  const { isFetching, data, refetch } = useQuery(['invoice-details'], async () => {
    const result = await InvoiceService.getInvoiceDetails({ invoiceId: invoiceId });
    return result.data;
  });
  const getInvoiceDetails = data;

  const invoiceStatus = getInvoiceDetails?.status?.toLowerCase?.() || '';
  const [isDownloading, setIsDownloading] = useState(false);

  const isApproved = invoiceStatus === 'approved';
  const isRejected = invoiceStatus === 'rejected';

  const isDraft = invoiceStatus === 'draft';
  const isSubmitted = invoiceStatus === 'submitted';

  // const isCompleted = invoiceStatus === 'completed';
  const isCompleted =
    invoiceStatus === 'completed' || invoiceStatus === 'pay_processed' || invoiceStatus === 'bill_processed';

  const accessAdmin = localStorage.getItem('userAccessAdmin');

  const adminPermissions = accessAdmin ? JSON.parse(accessAdmin) : [];

  const hasOverridePermission = adminPermissions.some(
    (permission: any) => permission.adminPermissionId === 'admin_override_permission'
  );
  console.log('hasOverridePermission', hasOverridePermission);

  const EditInvoice = (invoiceId: any) => {
    navigate(`/invoices/${invoiceId}/edit?action=edit`);
  };

  const ResubmitInvoice = (invoiceId: any) => {
    navigate(`/invoices/${invoiceId}/edit?action=resubmit`);
  };

  const OverrideInvoice = (invoiceId: any) => {
    navigate(`/invoices/${invoiceId}/override`);
  };

  const ApproveInvoice = (invoiceId: any) => {
    setIsOpen(true);
  };

  const RejectInvoice = (invoiceId: any) => {
    setIsOpenReject(true);
  };

  const DeleteInvoice = (invoiceId: any) => {
    setIsOpenDelete(true);
  };

  const handleBackClick = () => {
    navigate('/invoices');
  };

  const handleDownload = async () => {
    if (!invoiceId) return;

    try {
      setIsDownloading(true);

      // Fetch the ZIP blob
      const blob = await InvoiceService.getInvoicePdfDownload({ invoiceId });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Use .zip so browser treats it correctly
      link.download = `Invoice-${invoiceId}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatToMMDDYYYY = (dateString?: string | null): string => {
    if (!dateString) return '';

    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount?: number | string | null, currency?: string | null) => {
    if (amount === null || amount === undefined || amount === '') return '-';

    const numericAmount = Number(amount);

    if (isNaN(numericAmount)) return '-';

    // Handle null, undefined, and empty string
    const currencyCode = currency && currency.trim() ? currency : 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(numericAmount);
  };

  return (
    <MainContainer>
      <InvoiceSubmmisionModal isOpen={isOpen} setIsOpen={setIsOpen} invoiceDetail={getInvoiceDetails} />
      <InvoiceRejectionModal isOpen={isOpenReject} setIsOpen={setIsOpenReject} invoiceDetail={getInvoiceDetails} />
      <InvoiceDeleteModal isOpen={isOpenDelete} setIsOpen={setIsOpenDelete} invoiceDetail={getInvoiceDetails} />

      <TopBar>
        <SearchLink>
          <Link to={`/invoices`}>Search Invoices</Link>
        </SearchLink>

        <div style={{ display: 'flex', gap: '12px' }}>
          <ViewHistory style={{ marginTop: '14px' }}>
            <Link to={`/invoices/${invoiceId}/history`}>View History</Link>
          </ViewHistory>
          <RegularButton onClick={handleDownload}>Download PDF</RegularButton>
        </div>
      </TopBar>

      <InvoiceTitle>Invoice for {data?.invoiceDate}</InvoiceTitle>
      <SubText>{data?.employeeName}</SubText>

      <InfoGrid>
        <InfoRow>
          <Label>Status</Label>
          <Value>
            <Status>{data?.status}</Status>
          </Value>
        </InfoRow>

        <InfoRow>
          <Label>Client</Label>
          <Value>{data?.clientName}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Invoice ID</Label>
          <Value>
            {data?.invoiceShowId} {`(Version ${data?.version})`}{' '}
          </Value>
        </InfoRow>

        <InfoRow>
          <Label>Assignment</Label>
          <Value>{data?.assignmentName}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Invoice Number</Label>
          <Value>{data?.invoiceNumber}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Invoice Period</Label>
          {data?.periodStart && data?.periodEnd && (
            <Value>
              {formatPeriodDate(data.periodStart)} - {formatPeriodDate(data.periodEnd)}
            </Value>
          )}
        </InfoRow>

        <InfoRow>
          <Label>Approver</Label>
          <Value>{data?.managerName}</Value>
        </InfoRow>
        <InfoRow>
          <Label>Backup Approver</Label>
          <Value>{data?.coApproverName}</Value>
        </InfoRow>

        {invoiceStatus !== 'draft' && invoiceStatus !== 'submitted' && (
          <InfoRow>
            {data?.rejectedDate ? (
              <>
                <Label>Rejected At</Label>
                <Value style={{ color: 'red' }}>
                  {formatPeriodDate(data?.rejectedDate)} by {data?.rejectedby}{' '}
                </Value>
              </>
            ) : (
              <>
                <Label>Approved At</Label>
                <Value>
                  {formatToMMDDYYYY(data?.approvedDate)} by {data?.approvedby}{' '}
                </Value>
              </>
            )}
          </InfoRow>
        )}
      </InfoGrid>

      <Divider />

      <AmountGrid>
        <InfoRow>
          <Label>Billable Amount</Label>
          <Value>{formatCurrency(data?.billableAmount, data?.currency)}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Submitted Amount</Label>
          <Value>{formatCurrency(data?.submittedAmount, data?.currency)}</Value>
        </InfoRow>

        <InfoRow>
          <Label>Total Units/Hours</Label>
          <Value>{data?.totalUnits}</Value>
        </InfoRow>
      </AmountGrid>

      <Divider />

      <Label>Notes</Label>
      <NotesBox value={data?.notes} />

      <Label style={{ marginTop: '24px' }}>Attachments</Label>

      <div>
        <p>
          {data?.attachments && data.attachments.length > 0 && (
            <img src={attachmentBlue} alt="Formatting Icon" style={{ height: '17px', margin: '0px 10px 0px 2px' }} />
          )}
          {data?.attachments &&
            data?.attachments.length > 0 &&
            data?.attachments.map((attachment, index) => (
              <React.Fragment key={index}>
                <a href={attachment.attachmentUrl} target="_blank" rel="noopener noreferrer">
                  {attachment.attachmentName}
                </a>
                {index < (data?.attachments || []).length - 1 && ', '}
              </React.Fragment>
            ))}
        </p>
      </div>

      <Divider />
      {isDraft ? (
        <>
          <RegularButton onClick={() => EditInvoice(invoiceId)}>Edit Invoice</RegularButton>
          {hasOverridePermission && (
            <RegularButton onClick={() => OverrideInvoice(invoiceId)}>Override Invoice</RegularButton>
          )}
          <RegularButton onClick={() => DeleteInvoice(invoiceId)}>Delete Invoice</RegularButton>
        </>
      ) : isCompleted ? (
        <>
          <RegularButton onClick={() => ResubmitInvoice(invoiceId)}>Resubmit Invoice</RegularButton>
          {hasOverridePermission && (
            <RegularButton onClick={() => OverrideInvoice(invoiceId)}>Override Invoice</RegularButton>
          )}
          <RegularButton onClick={() => DeleteInvoice(invoiceId)}>Delete Invoice</RegularButton>
        </>
      ) : (
        <>
          {!isApproved && <RegularButton onClick={() => EditInvoice(invoiceId)}>Edit Invoice</RegularButton>}
          {!isRejected && <RegularButton onClick={() => RejectInvoice(invoiceId)}>Reject Invoice</RegularButton>}
          {!isApproved && <RegularButton onClick={() => ApproveInvoice(invoiceId)}>Approve Invoice</RegularButton>}

          {isApproved && <RegularButton onClick={() => ResubmitInvoice(invoiceId)}>Resubmit Invoice</RegularButton>}

          {hasOverridePermission && (
            <RegularButton onClick={() => OverrideInvoice(invoiceId)}>Override Invoice</RegularButton>
          )}
          <RegularButton onClick={() => DeleteInvoice(invoiceId)}>Delete Invoice</RegularButton>
        </>
      )}
    </MainContainer>
  );
};

export default ViewInvoice;
